'use client';

import { Check, X } from 'lucide-react';

const rows = [
  {
    key: 'speed',
    label: 'Setup Time',
    shortLabel: 'Speed',
    old: 'Hours of manual mapping.',
    tally: '60 Seconds. Drag, drop, done.',
  },
  {
    key: 'detection',
    label: 'Error Detection',
    shortLabel: 'Detection',
    old: 'Manual. You spot it.',
    tally: "Automated. We flag the 5 'Leakage' signs.",
  },
  {
    key: 'compliance',
    label: 'CMS Compliance',
    shortLabel: 'Compliance',
    old: 'None. Manual lookups.',
    tally: 'Built-in. 2026 Mandates pre-loaded.',
  },
  {
    key: 'disputes',
    label: 'Dispute Process',
    shortLabel: 'Disputes',
    old: 'Dreaded emails from scratch.',
    tally: '1-Click. Data-backed packets.',
  },
  {
    key: 'bank',
    label: 'Bank Verification',
    shortLabel: 'Bank Match',
    old: 'Impossible manual logs.',
    tally: 'Plaid Sync. Real-time matching.',
  },
  {
    key: 'cost',
    label: 'Cost',
    shortLabel: 'Cost',
    old: "'Free' (but costs $2k+ in missed pay).",
    tally: '$40/mo (Pays for itself in 1 audit).',
  },
];

export default function ComparisonSection() {
  return (
    <section className="mt-20 md:mt-36 pt-10">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex flex-col items-center text-center mb-6">
          <h2 className="font-poppins text-[32px] md:text-[53px] !font-bold md:!font-semibold tracking-tight leading-[1.2] text-black max-w-2xl mx-auto mb-5">
            The old spreadsheet grind{' '}
            <span className="text-[#236434]">vs.</span>{' '}
            the Tally way
          </h2>
          <p className="text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed mb-10">
            See how much friction disappears when your commission audits, bank match, and disputes run on rails instead
            of rows.
          </p>
        </div>

        {/* Cards wrapper – spacing below subhead matches pricing header/toggle */}
        {/* Mobile: full-bleed black Tally card with before/after and sticky founder bar */}
        <div className="md:hidden space-y-4 -mx-4">
          <div className="rounded-none bg-[#001f14] text-white shadow-[0_24px_60px_rgba(0,0,0,0.45)] ring-1 ring-[#003523] flex flex-col">
            <div className="px-5 pt-6 pb-3 border-b border-[#0b3b2a] relative">
              <div className="absolute -top-4 left-5 inline-flex items-center gap-2 rounded-full bg-[#ffe889] text-[#1a1a1a] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] shadow-md">
                Founder Rate
              </div>
              <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#66d396] mb-2">
                The Tally way
              </p>
              <h3 className="font-poppins text-[18px] font-semibold text-white">
                From spreadsheets to smart audits
              </h3>
            </div>
            <ul className="flex-1 divide-y divide-[#0b3b2a]">
              {rows.map((row) => (
                <li key={row.key} className="px-5 py-3 flex items-start gap-3">
                  <div className="mt-1 text-[#66d396] flex-shrink-0">
                    <Check className="w-6 h-6" strokeWidth={2.4} />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <p className="font-poppins text-[12px] font-semibold text-[#e5f6ef]">
                      {row.shortLabel}
                    </p>
                    <p className="font-body text-[12px] text-slate-100/90 leading-snug">
                      {row.tally}
                    </p>
                    <p className="font-body text-[10px] text-slate-400 leading-snug">
                      <span className="font-semibold text-slate-300">Before:</span> {row.old}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="px-5 pt-2 pb-4 border-t border-[#0b3b2a]">
              <p className="font-body text-[14px] text-slate-200/90 leading-relaxed">
                <span className="font-semibold text-[#66d396]">On average, independent agents recover 12% more revenue</span>{' '}
                in their first 90 days using Tally.
              </p>
            </div>
          </div>

          {/* Sticky founder rate bar on mobile */}
          <div className="sticky bottom-4 left-0 right-0">
            <div className="mx-auto max-w-md rounded-2xl bg-[#001f14] shadow-[0_18px_40px_rgba(0,0,0,0.6)] border border-[#0b3b2a] px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="font-poppins text-[20px] font-semibold text-[#66d396] leading-tight">
                  $40/mo Founder Price
                </p>
                <p className="font-body text-[11px] text-slate-200/80 leading-snug">
                  Pays for itself in a single recovered audit.
                </p>
              </div>
              <a
                href="/public-subscription-selection"
                className="inline-flex items-center justify-center px-3 py-2 rounded-[999px] bg-[#66d396] text-[#001f14] text-[11px] font-semibold text-center"
              >
                Lock in Early Access rate
              </a>
            </div>
          </div>
        </div>

        {/* Desktop: side-by-side comparison */}
        <div className="hidden md:grid md:grid-cols-2 gap-8 items-stretch">
          {/* Old Way */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 text-slate-800 shadow-sm flex flex-col">
            <div className="px-6 pt-10 pb-4 border-b border-slate-200/80">
              <p className="text-[12px] font-semibold tracking-[0.18em] uppercase text-slate-500 mb-1">
                The old way
              </p>
              <h3 className="font-poppins text-[22px] md:text-[24px] font-semibold text-slate-800">
                Spreadsheets & manual checks
              </h3>
            </div>
            <ul className="flex-1 divide-y divide-slate-200">
              {rows.map((row) => (
                <li key={row.key} className="px-6 py-4 flex items-start gap-3">
                  <div className="mt-0.5 text-slate-400 flex-shrink-0">
                    <X className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-poppins text-[13px] font-semibold text-slate-700 mb-1">
                      {row.label}
                    </p>
                    <p className="font-body text-[13px] text-slate-600 leading-snug">{row.old}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Tally Way */}
          <div className="rounded-2xl bg-[#001f14] text-white shadow-[0_24px_60px_rgba(0,0,0,0.45)] ring-1 ring-[#003523] flex flex-col">
            <div className="px-6 pt-10 pb-4 border-b border-[#0b3b2a] relative">
              <div className="absolute -top-4 left-6 inline-flex items-center gap-2 rounded-full bg-[#ffe889] text-[#1a1a1a] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] shadow-md">
                Founder Rate
              </div>
              <p className="text-[12px] font-semibold tracking-[0.18em] uppercase text-[#66d396] mb-2">
                The Tally way
              </p>
              <h3 className="font-poppins text-[22px] md:text-[24px] font-semibold text-white">
                Automated Commission Audit & Recovery
              </h3>
            </div>
            <ul className="flex-1 divide-y divide-[#0b3b2a]">
              {rows.map((row) => (
                <li key={row.key} className="px-6 py-4 flex items-start gap-3">
                  <div className="mt-0.5 text-[#66d396] flex-shrink-0">
                    <Check className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-poppins text-[13px] font-semibold text-[#e5f6ef] mb-1">
                      {row.label}
                    </p>
                    <p className="font-body text-[13px] text-slate-100/90 leading-snug">{row.tally}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="px-6 pt-3 pb-5 border-t border-[#0b3b2a]">
              <p className="font-body text-base md:text-lg text-slate-200/90 leading-relaxed">
                <span className="font-semibold text-[#66d396]">On average, independent agents recover 12% more revenue</span>{' '}
                in their first 90 days using Tally.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

